import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlatformCard = ({ 
  platform, 
  isSelected, 
  onSelect, 
  config, 
  onConfigChange,
  currency,
  vatEnabled 
}) => {
  const formatCurrency = (amount) => {
    const symbol = currency === 'USD' ? '$' : 'R';
    return `${symbol}${amount?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calculateVAT = (amount) => {
    return vatEnabled ? amount * 0.15 : 0;
  };

  const calculateTotal = () => {
    let total = 0;
    
    if (platform === 'woocommerce') {
      total += parseFloat(config?.hostingCost || 0);
      total += parseFloat(config?.shippingCost || 0);
      if (config?.customDevelopment) total += 2000;
    } else {
      const plans = { basic: 700, standard: 1900, advanced: 7200 };
      total += plans?.[config?.selectedPlan] || 0;
    }
    
    const vat = calculateVAT(total);
    return { subtotal: total, vat, total: total + vat };
  };

  const totals = calculateTotal();

  return (
    <div className={`bg-card border-2 rounded-lg p-6 transition-all duration-200 ${
      isSelected ? 'border-primary shadow-lg' : 'border-border hover:border-primary/50'
    }`}>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
            platform === 'woocommerce' ? 'bg-purple-500/10 text-purple-600' : 'bg-green-500/10 text-green-600'
          }`}>
            <Icon name={platform === 'woocommerce' ? 'ShoppingCart' : 'Store'} size={24} />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {platform === 'woocommerce' ? 'WooCommerce' : 'Shopify'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {platform === 'woocommerce' ? 'Self-hosted solution' : 'Hosted platform'}
            </p>
          </div>
        </div>
        <Button
          variant={isSelected ? 'default' : 'outline'}
          size="sm"
          onClick={onSelect}
          iconName={isSelected ? 'Check' : 'Plus'}
          iconPosition="left"
        >
          {isSelected ? 'Selected' : 'Select'}
        </Button>
      </div>
      {platform === 'woocommerce' ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Hosting Cost (Monthly)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currency === 'USD' ? '$' : 'R'}
              </span>
              <input
                type="number"
                value={config?.hostingCost || ''}
                onChange={(e) => onConfigChange({ ...config, hostingCost: e?.target?.value })}
                placeholder="0.00"
                className="w-full pl-8 pr-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Shipping Cost (Monthly)
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {currency === 'USD' ? '$' : 'R'}
              </span>
              <input
                type="number"
                value={config?.shippingCost || ''}
                onChange={(e) => onConfigChange({ ...config, shippingCost: e?.target?.value })}
                placeholder="0.00"
                className="w-full pl-8 pr-3 py-2 bg-background border border-input rounded-md text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Gateway Percentage
            </label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min="0"
                max="5"
                step="0.1"
                value={config?.gatewayPercentage || 2.9}
                onChange={(e) => onConfigChange({ ...config, gatewayPercentage: parseFloat(e?.target?.value) })}
                className="flex-1"
              />
              <span className="text-sm font-medium text-foreground w-12 text-right">
                {(config?.gatewayPercentage || 2.9)?.toFixed(1)}%
              </span>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-muted rounded-md">
            <div className="flex items-center gap-2">
              <Icon name="Code" size={16} className="text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Custom Development</span>
            </div>
            <button
              onClick={() => onConfigChange({ ...config, customDevelopment: !config?.customDevelopment })}
              className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${
                config?.customDevelopment ? 'bg-primary' : 'bg-input'
              }`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform duration-200 ${
                config?.customDevelopment ? 'translate-x-5' : 'translate-x-0'
              }`} />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">
              Select Plan
            </label>
            <div className="space-y-2">
              {[
                { id: 'basic', name: 'Basic', price: 700, fee: 2.0, features: ['Online store', 'Unlimited products', '2 staff accounts'] },
                { id: 'standard', name: 'Standard', price: 1900, fee: 1.0, features: ['Basic features', 'Professional reports', '5 staff accounts'] },
                { id: 'advanced', name: 'Advanced', price: 7200, fee: 0.6, features: ['Standard features', 'Advanced reports', '15 staff accounts'] }
              ]?.map((plan) => (
                <button
                  key={plan?.id}
                  onClick={() => onConfigChange({ ...config, selectedPlan: plan?.id })}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${
                    config?.selectedPlan === plan?.id
                      ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-foreground">{plan?.name}</span>
                    <span className="text-sm font-medium text-primary">
                      {formatCurrency(plan?.price)}/mo
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name="CreditCard" size={14} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      {plan?.fee}% transaction fee
                    </span>
                  </div>
                  <ul className="space-y-1">
                    {plan?.features?.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-muted-foreground">
                        <Icon name="Check" size={12} className="text-success" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="mt-6 pt-6 border-t border-border space-y-2">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Subtotal</span>
          <span className="font-medium text-foreground">{formatCurrency(totals?.subtotal)}</span>
        </div>
        {vatEnabled && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">VAT (15%)</span>
            <span className="font-medium text-foreground">{formatCurrency(totals?.vat)}</span>
          </div>
        )}
        <div className="flex items-center justify-between text-base pt-2 border-t border-border">
          <span className="font-semibold text-foreground">Total Monthly Cost</span>
          <span className="font-semibold text-primary">{formatCurrency(totals?.total)}</span>
        </div>
      </div>
    </div>
  );
};

export default PlatformCard;